"use client";

import {
  EntityInstance,
  FilterInput,
  getEntityInstanceList,
  PaginationData,
} from "@/services/entity-instance.service";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEntitySchemaByName } from "./schema/use-entity-schema-by-name";
import { getSubscriptionFieldsSelection } from "@/services/schema.service";
import { useFieldNameStore } from "@/stores/entity-instance-field-store";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useEntitySubscription } from "./subscriptions/use-entity-subscription";
import type { EventMembership } from "@/lib/subscriptions/subscription-manager";
import type { EntityEventKind } from "@/services/subscription.service";

type ListPage = { entityInstances: EntityInstance[]; pagination?: PaginationData };
type InfiniteListData = { pages: ListPage[]; pageParams: unknown[] };

/**
 * Resolve a (possibly dotted) sort-field path against a row, transparently
 * unwrapping the `{ data: … }` envelopes relation values are nested in, so
 * "Marke.Name" reads row.Marke.data.Name. Returns undefined when the path
 * isn't present in the row's projection.
 */
const readFieldPath = (obj: unknown, path: string): unknown => {
  let current: unknown = obj;
  for (const part of path.split(".")) {
    if (current && typeof current === "object" && "data" in current) {
      current = (current as { data: unknown }).data;
    }
    if (!current || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
};

export const useEntityInstanceList = ({
  entityName,
  filter,
  fetchAllFields = true,
  fetchDeepRelations = false,
  fieldsToFetch,
  enabled = true,
}: {
  entityName: string;
  filter?: FilterInput;
  fetchAllFields?: boolean;
  fetchDeepRelations?: boolean;
  fieldsToFetch?: string[];
  enabled?: boolean;
}) => {
  const { entitySchemaWithRelations } = useEntitySchemaByName(entityName);

  const fieldNameStore = useFieldNameStore();

  const paginationVariables = useMemo(() => {
    if (fetchAllFields) {
      const paginationVariables = entitySchemaWithRelations?.fields.reduce((acc, field) => {
        if (field.type?.endsWith("ListData")) {
          return {
            ...acc,
            [`${field.name}Pagination`]: {
              page: 1,
              per_page: 3, // Fetch first 3 items to display in the cell
            },
          };
        }
        return acc;
      }, {});
      return paginationVariables;
    }

    // When not fetching all fields, check if any fieldsToFetch are to-many relations
    if (fieldsToFetch && entitySchemaWithRelations?.fields) {
      const relationFields = fieldsToFetch
        .filter((field) => field.includes("."))
        .map((field) => field.split(".")[0]);

      const uniqueRelationFields = [...new Set(relationFields)];

      const paginationVars = uniqueRelationFields.reduce(
        (acc, fieldName) => {
          const fieldSchema = entitySchemaWithRelations.fields.find((f) => f.name === fieldName);
          if (fieldSchema?.type?.endsWith("ListData")) {
            return {
              ...acc,
              [`${fieldName}Pagination`]: {
                page: 1,
                per_page: 3, // Fetch first 3 items to display in the cell
              },
            };
          }
          return acc;
        },
        {} as Record<string, { page: number; per_page: number }>,
      );

      return paginationVars;
    }

    return {};
  }, [fetchAllFields, entitySchemaWithRelations, fieldsToFetch]);

  const fieldsToFetchForRelationFieldNames = useMemo(() => {
    const fieldNameMap: Array<{
      fieldName: string;
      fieldsToFetch: Array<string>;
    }> = [];

    if (fetchAllFields) {
      entitySchemaWithRelations?.fields.forEach((field) => {
        if (field.type?.endsWith("ListData") && !fetchDeepRelations) {
          fieldNameMap.push({
            fieldName: field.name,
            fieldsToFetch: ["id"],
          });
        } else if (field.kind === "OBJECT") {
          fieldNameMap.push({
            fieldName: field.name,
            fieldsToFetch: field.fields?.map((field) => field.name) ?? [],
          });
        }
      });
    } else {
      const relationFields = fieldsToFetch?.filter((field) => field.includes("."));
      relationFields?.forEach((field) => {
        const [relationField, ...rest] = field.split(".");
        // Keep the full sub-path (everything after the first segment). For a single-hop
        // path this is just the leaf ("name"); for a deeper to-one chain it stays dotted
        // ("address.city") so the query builder can emit the nested selection.
        const relationFieldToFetch = rest.join(".");

        const existingRelationField = fieldNameMap.find((f) => f.fieldName === relationField);
        if (existingRelationField) {
          if (!existingRelationField.fieldsToFetch.includes(relationFieldToFetch)) {
            existingRelationField.fieldsToFetch.push(relationFieldToFetch);
          }
        } else {
          fieldNameMap.push({
            fieldName: relationField,
            // Always include `id` alongside the displayed leaf so relation items
            // can be identified (inline editing, connect/disconnect, dedup) — the
            // leaf value alone isn't a stable identity.
            fieldsToFetch: relationFieldToFetch === "id" ? ["id"] : ["id", relationFieldToFetch],
          });
        }
      });

      // const relationFields = entitySchemaWithRelations?.fields
      //   .filter((field) => field.kind === 'OBJECT')
      //   .filter(
      //     (field) =>
      //       field.type?.endsWith('Data') && !field.type.endsWith('ListData'),
      //   )
      //   .map((field) => {
      //     return (
      //       field.name.replace('Data', '').charAt(0).toUpperCase() +
      //       field.name.replace('Data', '').slice(1)
      //     );
      //   });
      // relationFields?.forEach((field) => {
      //   const fieldsToFetchForThat = fieldNameStore.eilStates[field]?.field;
      //   if (fieldsToFetchForThat) {
      //     fieldNameMap.push({
      //       fieldName: field,
      //       fieldsToFetch: [fieldsToFetchForThat],
      //     });
      //   }
      // });
    }

    return fieldNameMap;
  }, [
    fetchAllFields,
    entitySchemaWithRelations?.fields,
    fetchDeepRelations,
    fieldNameStore.eilStates,
    fieldsToFetch,
  ]);

  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => [
      "entity-instance-list",
      entityName,
      filter,
      fieldsToFetchForRelationFieldNames.flatMap((x) => x.fieldsToFetch),
      fieldsToFetch?.slice().sort().join(","), // sorted so a mere reorder doesn't refetch
    ],
    [entityName, filter, fieldsToFetchForRelationFieldNames, fieldsToFetch],
  );

  const result = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }) =>
      await getEntityInstanceList({
        entityName: entityName,
        cursor: pageParam,
        entitySchema: entitySchemaWithRelations,
        filter,
        fetchRelations: fetchAllFields,
        fieldsToFetchForRelationFieldNames,
        paginationVariables,
        fieldsToFetch,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.has_more_pages ? lastPage.pagination.current_page + 1 : undefined,
    initialPageParam: 1,
    enabled: !!entitySchemaWithRelations && !!fieldsToFetch?.length && enabled,
    // Live updates arrive via the entity subscription below, so we don't need to
    // refetch this (potentially large, paginated) list every time the tab regains
    // focus.
    refetchOnWindowFocus: false,
  });

  // Per-event projection for the subscription: built by the SAME builder as the
  // list query itself (variants, relation sub-selections, media fields), so event
  // payloads have the exact row shape and slot straight into the cache. Only the
  // to-many pagination args are inlined since the stored selection is replayed
  // by the websocket-service outside this document. `id` is always included by
  // the builder (needed to route/merge events).
  const fieldsSelection = useMemo(() => {
    if (!fieldsToFetch?.length || !entitySchemaWithRelations) return "";
    return getSubscriptionFieldsSelection({
      entitySchema: entitySchemaWithRelations,
      fieldsToFetch,
      fieldsToFetchForRelationFieldNames,
    });
  }, [fieldsToFetch, entitySchemaWithRelations, fieldsToFetchForRelationFieldNames]);

  // A local insert only knows the right position under the default newest-first
  // orders; anything else (scalar sorts, relation-path sorts) must ask the
  // server. No sortby counts as newest-first because that's the backend default.
  const isNewestFirstSort = useMemo(() => {
    const primary = filter?.sortby?.[0];
    if (!primary) return true;
    return (
      (primary.field === "integer_id" || primary.field === "created_at") && primary.order === "desc"
    );
  }, [filter?.sortby]);

  // Debounced list refetch for events a local cache patch can't place correctly
  // (row entered the filter window, create under a non-trivial sort, or an
  // update to a row on a not-yet-loaded page). Trailing debounce batches event
  // bursts into at most one refetch of the loaded pages per window. A caller
  // that needs the refetch to feel immediate (sort-value changes, where the
  // row visibly moves) can pass a shorter delay — an earlier deadline wins
  // over a pending later one, never the other way around.
  const invalidateTimerRef = useRef<{
    timer: ReturnType<typeof setTimeout>;
    fireAt: number;
  } | null>(null);
  const scheduleListInvalidate = useCallback(
    (delayMs = 1500) => {
      const fireAt = Date.now() + delayMs;
      const pending = invalidateTimerRef.current;
      if (pending && pending.fireAt <= fireAt) return;
      if (pending) clearTimeout(pending.timer);
      invalidateTimerRef.current = {
        fireAt,
        timer: setTimeout(() => {
          invalidateTimerRef.current = null;
          queryClient.invalidateQueries({ queryKey });
        }, delayMs),
      };
    },
    [queryClient, queryKey],
  );
  useEffect(
    () => () => {
      if (invalidateTimerRef.current) clearTimeout(invalidateTimerRef.current.timer);
    },
    [],
  );

  // Patch the infinite-query cache in place from a subscription event instead of
  // refetching: update merges by id; delete — and an update whose row left this
  // subscription's result set (membership.inResultSet === false) — removes by
  // id; create prepends when the sort allows it. Everything a local patch can't
  // place correctly falls back to the debounced refetch above.
  const onEntityEvent = useCallback(
    (kind: EntityEventKind, data: Record<string, unknown>, membership?: EventMembership) => {
      const id = data?.id as string | undefined;
      if (!id) return;

      const bumpTotal = (pagination: PaginationData | undefined, delta: number) =>
        pagination
          ? { ...pagination, total: Math.max(0, (pagination.total ?? 0) + delta) }
          : pagination;

      const current = queryClient.getQueryData<InfiniteListData>(queryKey);
      if (!current?.pages?.length) return;
      const exists = current.pages.some((page) =>
        page.entityInstances.some((row) => row?.id === id),
      );

      // Removal: a genuine delete, or an update after which the row is no
      // longer visible under this subscription's filter/permissions.
      if (kind === "delete" || membership?.inResultSet === false) {
        if (!exists) return;
        queryClient.setQueryData<InfiniteListData>(queryKey, (old) => {
          if (!old?.pages?.length) return old;
          const pages = old.pages.map((page) => {
            const next = page.entityInstances.filter((row) => row?.id !== id);
            return next.length === page.entityInstances.length
              ? page
              : { ...page, entityInstances: next };
          });
          pages[0] = { ...pages[0], pagination: bumpTotal(pages[0].pagination, -1) };
          return { ...old, pages };
        });
        // The optimistic removal shifts server-side page boundaries: the row
        // that slides backward across the last loaded page's boundary would be
        // skipped by the next scroll-fetch (a hole — dedup can't fix those,
        // unlike insert duplicates). Refetch the loaded pages behind the
        // instant patch to rebalance boundaries and reconcile the total.
        scheduleListInvalidate();
        return;
      }

      // create or update on a loaded row (e.g. the echo of our own optimistic
      // write). Three cases against the list's sort keys (immutable ones —
      // integer_id/created_at/id — can't move a row and are ignored):
      //  - sort value definitely changed: DON'T merge — the row is about to
      //    move, and value+position should change together, not value first
      //    and a jump later. A near-immediate refetch delivers both at once.
      //  - sort value not comparable (mutable sort field outside the
      //    projection): merge for instant cells, refetch lazily to be safe.
      //  - sort value unchanged (the common case): merge in place, done.
      if (exists) {
        const prevRow = current.pages
          .flatMap((page) => page.entityInstances)
          .find((row) => row?.id === id);
        let sortChange: "changed" | "unknown" | "unchanged" = "unchanged";
        for (const sort of filter?.sortby ?? []) {
          const field = sort.field;
          if (field === "integer_id" || field === "created_at" || field === "id") continue;
          const before = readFieldPath(prevRow, field);
          const after = readFieldPath(data, field);
          if (before === undefined && after === undefined) {
            sortChange = "unknown";
            continue;
          }
          if (before !== after) {
            sortChange = "changed";
            break;
          }
        }

        if (sortChange === "changed") {
          scheduleListInvalidate(250);
          return;
        }

        queryClient.setQueryData<InfiniteListData>(queryKey, (old) => {
          if (!old?.pages?.length) return old;
          const pages = old.pages.map((page) => {
            let changed = false;
            const next = page.entityInstances.map((row) => {
              if (row?.id !== id) return row;
              changed = true;
              return { ...row, ...data };
            });
            return changed ? { ...page, entityInstances: next } : page;
          });
          return { ...old, pages };
        });
        if (sortChange === "unknown") scheduleListInvalidate();
        return;
      }

      // create under newest-first sort: a brand-new row provably belongs at the
      // top — prepend to the first page and bump the visible total.
      if (kind === "create" && isNewestFirstSort) {
        queryClient.setQueryData<InfiniteListData>(queryKey, (old) => {
          if (!old?.pages?.length) return old;
          const [firstPage, ...rest] = old.pages;
          const newFirst: ListPage = {
            ...firstPage,
            entityInstances: [data as EntityInstance, ...firstPage.entityInstances],
            pagination: bumpTotal(firstPage.pagination, 1),
          };
          return { ...old, pages: [newFirst, ...rest] };
        });
        return;
      }

      // Row we don't have, at a position we can't compute (entered the filter
      // window, create under a non-trivial sort, or lives on an unloaded page)
      // — let the server re-derive the loaded pages.
      scheduleListInvalidate();
    },
    [queryClient, queryKey, isNewestFirstSort, scheduleListInvalidate],
  );

  useEntitySubscription({
    entityName,
    fieldsSelection,
    filter,
    onEvent: onEntityEvent,
    // On a genuine reconnect, in-place cache patching may have missed events that
    // fell outside the backend's queue window. Refetch this exact list to backfill.
    onResync: () => queryClient.invalidateQueries({ queryKey }),
    enabled: enabled && !!entitySchemaWithRelations && !!fieldsToFetch?.length && !!fieldsSelection,
  });

  return result;
};
